/**
 * Type definitions and constants for Salesforce Support Chat
 * Centralizes all magic strings and numbers
 */

// ======================
// CONVERSATION CONSTANTS
// ======================

/** A single listener id for network monitoring set -> no duplicated listener */
export const SUPPORT_CHAT_NETWORK_LISTENER_ID = 'support-chat-network-listener';

export const CONVERSATION_STATUS = {
  NOT_STARTED: 'not_started',
  ACTIVE: 'active',
  ENDED: 'ended',
  ERROR: 'error'
};

export const CONNECTION_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

// ======================
// MESSAGE TYPES
// ======================

export const MESSAGE_TYPE = {
  TEXT: 'text',
  SYSTEM: 'system',
  TYPING: 'typing',
  PARTICIPANT_CHANGED: 'participantChanged',
  CONVERSATION_ENDED: 'conversationEnded',
  CHAT_ENDED: 'chatEnded',
  CHAT_MESSAGE: 'chatMessage',
  CHAT_REQUEST_SUCCESS: 'chatRequestSuccess',
  CHAT_REQUEST_FAIL: 'chatRequestFail',
  CHAT_TRANSFER_SUCCESS: 'chatTransferSuccessful',
  ROUTING_WORK_BEGIN: 'RoutingWorkBegin',
  ROUTING_WORK_END: 'RoutingWorkEnd'
};

// ======================
// EVENT TYPES (Salesforce API)
// ======================

export const SALESFORCE_EVENT_TYPES = {
  CONVERSATION_MESSAGE: 'ConversationMessage',
  PARTICIPANT_CHANGED: 'ParticipantChanged',
  TYPING_STARTED_INDICATOR: 'TypingStartedIndicator',
  TYPING_STOPPED_INDICATOR: 'TypingStoppedIndicator',
  ROUTING_RESULT: 'RoutingResult',
  CHAT_ENDED: 'ChatEnded',
  CHAT_MESSAGE: 'ChatMessage',
  CHAT_REQUEST_SUCCESS: 'ChatRequestSuccess',
  CHAT_REQUEST_FAIL: 'ChatRequestFail',
  CHAT_TRANSFER_SUCCESSFUL: 'ChatTransferSuccessful'
};

// ======================
// MESSAGE STATUS
// ======================

export const MESSAGE_STATUS = {
  SENDING: 'Sending',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
  READ: 'Read'
};

// ======================
// PARTICIPANT ROLES
// ======================

export const PARTICIPANT_ROLE = {
  END_USER: 'EndUser',
  AGENT: 'Agent',
  SYSTEM: 'System',
  BOT: 'Bot'
};

// ======================
// ERROR CODES
// ======================

export const ERROR_CODE = {
  // Authentication errors
  AUTH_FAILED: 'AUTH_FAILED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  
  // Connection errors
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  EVENTSOURCE_ERROR: 'EVENTSOURCE_ERROR',
  
  // Conversation errors
  CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',
  CONVERSATION_ENDED: 'CONVERSATION_ENDED',
  CONVERSATION_ERROR: 'CONVERSATION_ERROR',
  
  // Message errors
  MESSAGE_SEND_FAILED: 'MESSAGE_SEND_FAILED',
  MESSAGE_TOO_LONG: 'MESSAGE_TOO_LONG',
  INVALID_MESSAGE: 'INVALID_MESSAGE',
  
  // API errors
  API_ERROR: 'API_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  
  // Storage errors
  STORAGE_ERROR: 'STORAGE_ERROR',
  STORAGE_FULL: 'STORAGE_FULL',
  
  // Configuration errors
  CONFIG_ERROR: 'CONFIG_ERROR',
  MISSING_CONFIG: 'MISSING_CONFIG'
};

// ======================
// CONFIGURATION CONSTANTS
// ======================

export const CONFIG = {
  // Timeouts (milliseconds)
  API_TIMEOUT: 30000,
  CONNECTION_TIMEOUT: 10000,
  RECONNECT_TIMEOUT: 3000,
  TYPING_INDICATOR_DEBOUNCE: 1000,
  MESSAGE_RETRY_DELAY: 2000,
  
  // Retry limits
  MAX_RETRIES: 5,
  MAX_MESSAGE_RETRIES: 3,
  MAX_RECONNECT_ATTEMPTS: 10,
  
  // Message limits
  MAX_MESSAGE_LENGTH: 3000,
  MAX_MESSAGES_TO_STORE: 500,
  
  // JWT
  JWT_REFRESH_THRESHOLD: 300000, // 5 minutes
  JWT_STORAGE_KEY: 'salesforce_jwt_token',
  CONVERSATION_ID_STORAGE_KEY: 'salesforce_conversation_id',
  LAST_EVENT_ID_STORAGE_KEY: 'salesforce_last_event_id',
  
  // Polling/Heartbeat
  HEARTBEAT_INTERVAL: 30000, // 30 seconds
  HEALTH_CHECK_INTERVAL: 60000, // 1 minute
  
  // UI
  SCROLL_DELAY: 100,
  ANIMATION_DURATION: 300,
  TOAST_DURATION: 5000
};

// ======================
// ROUTING ATTRIBUTES
// ======================

export const ROUTING_PRIORITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent'
};

export const ROUTING_DEPARTMENT = {
  SUPPORT: 'Support',
  SALES: 'Sales',
  BILLING: 'Billing',
  TECHNICAL: 'Technical'
};

// ======================
// STORAGE KEYS
// ======================

export const STORAGE_KEYS = {
  JWT_TOKEN: 'salesforce_jwt_token',
  CONVERSATION_ID: 'salesforce_conversation_id',
  LAST_EVENT_ID: 'salesforce_last_event_id',
  ROUTING_ATTRIBUTES: 'salesforce_routing_attributes',
  USER_INFO: 'salesforce_user_info',
  SESSION_ID: 'salesforce_session_id',
  DEBUG_MODE: 'SALESFORCE_DEBUG'
};

// ======================
// API ENDPOINTS
// ======================

export const API_PATHS = {
  AUTHENTICATED_TOKEN: '/iamessage/api/v2/authorization/authenticated/access-token',
  CONTINUATION_TOKEN: '/iamessage/api/v2/authorization/continuation-access-token',
  CONVERSATION: '/iamessage/api/v2/conversation',
  CONVERSATION_EVENTS: '/iamessage/api/v2/conversation/events',
  MESSAGE: '/iamessage/api/v2/conversation/:conversationId/message',
  TYPING: '/iamessage/api/v2/conversation/:conversationId/typing'
};

// ======================
// REGEX PATTERNS
// ======================

export const PATTERNS = {
  JWT: /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/,
  UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  URL: /^https?:\/\/.+/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
};

// ======================
// TYPE GUARDS
// ======================

/**
 * Check if status is a valid conversation status
 */
export const isConversationStatus = (status) => {
  return Object.values(CONVERSATION_STATUS).includes(status);
};

/**
 * Check if status is a valid connection status
 */
export const isConnectionStatus = (status) => {
  return Object.values(CONNECTION_STATUS).includes(status);
};

/**
 * Check if type is a valid message type
 */
export const isMessageType = (type) => {
  return Object.values(MESSAGE_TYPE).includes(type);
};

/**
 * Check if event type is a valid Salesforce event
 */
export const isSalesforceEventType = (type) => {
  return Object.values(SALESFORCE_EVENT_TYPES).includes(type);
};

/**
 * Validate JWT format
 */
export const isValidJWT = (token) => {
  return typeof token === 'string' && PATTERNS.JWT.test(token);
};

/**
 * Validate UUID format
 */
export const isValidUUID = (id) => {
  return typeof id === 'string' && PATTERNS.UUID.test(id);
};

// ======================
// UTILITY TYPES
// ======================

/**
 * Create a readonly version of an object
 */
export const freeze = (obj) => Object.freeze(obj);

/**
 * Get all values from a constant object
 */
export const getConstantValues = (constantObj) => Object.values(constantObj);

/**
 * Get all keys from a constant object
 */
export const getConstantKeys = (constantObj) => Object.keys(constantObj);

// Freeze all constants to prevent mutation
Object.freeze(CONVERSATION_STATUS);
Object.freeze(CONNECTION_STATUS);
Object.freeze(MESSAGE_TYPE);
Object.freeze(SALESFORCE_EVENT_TYPES);
Object.freeze(MESSAGE_STATUS);
Object.freeze(PARTICIPANT_ROLE);
Object.freeze(ERROR_CODE);
Object.freeze(CONFIG);
Object.freeze(ROUTING_PRIORITY);
Object.freeze(ROUTING_DEPARTMENT);
Object.freeze(STORAGE_KEYS);
Object.freeze(API_PATHS);
Object.freeze(PATTERNS);
